import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  List,
  RawButton,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { DEFAULT_EMPTY_CHARACTER } from '@/common/constants';
import { useFileDownloadByURL, useSnackbar } from '@/modules/core';
import { SnackbarState } from '@/modules/core/core.types';

import { ReportTab } from '../../report.types';
import { REPORT_TYPE_TITLES, ScheduledReportInboxItemPayload } from '../ScheduledReport.types';

import { useRemoveInboxSchedulerInboxQuery } from './queries';
import ScheduledReportInboxItemRemoveModal from './ScheduledReportInboxItemRemoveModal';
import ScheduledReportItemFilters from './ScheduledReportItemFilters';

const StyledText = styled(Text)`
  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;
interface Props extends ScheduledReportInboxItemPayload {}
const ScheduledInboxReportItem: React.FC<Props> = ({
  id,
  name,
  reportType,
  reportGroupType,
  url,
  generationDate,
  reportFilter,
  ...rest
}) => {
  const [isRemoveModal, setRemoveModal] = useState(false);
  const [isExpand, setExpand] = useState(false);
  const handleToggle = useCallback(() => {
    setExpand(!isExpand);
  }, [isExpand]);
  const currentReportType = useMemo(() => {
    return REPORT_TYPE_TITLES[reportType as string];
  }, [reportType]);

  const handleDownloadByUrl = useFileDownloadByURL();

  const handleDownload = useCallback(() => {
    if (url) {
      handleDownloadByUrl(url);
    }
  }, [handleDownloadByUrl, url]);

  const { handleAddSnackbar } = useSnackbar();

  const { isLoading: isRemoveLoading, handleSubmit: handleSubmitRemove } =
    useRemoveInboxSchedulerInboxQuery(id);
  const handleShowRemoveModal = useCallback(() => {
    setRemoveModal(true);
  }, []);
  const handleRemove = useCallback(async () => {
    await handleSubmitRemove();
    handleAddSnackbar({
      text: `${name} inbox was removed successfully`,
      state: SnackbarState.positive,
      closeIcon: true,
    });
    setRemoveModal(false);
  }, [handleAddSnackbar, handleSubmitRemove, name]);

  return (
    <List.Item
      testId={`scheduled-report_item_${id}`}
      direction="column"
      justify="center"
      // onClick={handleToggle}
    >
      <ScheduledReportInboxItemRemoveModal
        visible={isRemoveModal}
        isLoading={isRemoveLoading}
        onSetVisible={setRemoveModal}
        onRemoveSubmit={handleRemove}
      />
      <Box height={{ min: '40px' }} align="center" direction="row" fill>
        <Box direction="column" style={{ flex: 2 }}>
          <RawButton
            data-testId={`scheduled-report_item_button_${id}`}
            onClick={handleToggle}
            style={{ display: 'block' }}
          >
            <Box direction="row" align="center">
              {isExpand ? (
                <ChevronUpIcon size="24px" color="iconPrimary" />
              ) : (
                <ChevronDownIcon size="24px" color="iconPrimary" />
              )}
              <StyledText
                data-testid={`scheduled-report_item_${id}_name`}
                truncate
                title={name}
                weight={700}
                margin={{ left: 'spacing12' }}
              >
                {name}
              </StyledText>
            </Box>
          </RawButton>
        </Box>
        <Box width="12px" />

        <List.Item.Text testId={`scheduled-report_item_${id}_generation-date`} weight={400} flex={2}>
          {generationDate || DEFAULT_EMPTY_CHARACTER}
        </List.Item.Text>
        <List.Item.Text
          testId={`scheduled-report_item_${id}_report-type`}
          truncate
          title={currentReportType}
          weight={400}
          flex={2}
        >
          {currentReportType || DEFAULT_EMPTY_CHARACTER}
        </List.Item.Text>

        {url && (
          <Box margin={{ right: 'spacing8' }}>
            <RawButton
              data-testId={`scheduled-report_item_${id}_download-button`}
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
            >
              <DownloadIcon size="24px" color="iconAccent" />
            </RawButton>
          </Box>
        )}
        <List.Item.Options testId={`scheduled-report_item_${id}_options`}>
          <List.Item.Options.Item
            color="danger"
            testId={`scheduled-report_item_${id}_options_download`}
            onClick={handleShowRemoveModal}
          >
            Remove
          </List.Item.Options.Item>
        </List.Item.Options>
      </Box>
      {isExpand && (
        <>
          <Box height="spacing16" />
          <ScheduledReportItemFilters
            {...reportFilter}
            isWithSSN={reportGroupType === ReportTab.Report}
            testId={`scheduled-report_item_${id}_filters`}
          />
        </>
      )}
    </List.Item>
  );
};
export default ScheduledInboxReportItem;
